/**
 * Utility for mapping between state names and state codes
 */

export const STATE_NAME_TO_CODE: Record<string, string> = {
  'Alabama': 'AL',
  'Alaska': 'AK', 
  'Arizona': 'AZ',
  'Arkansas': 'AR',
  'California': 'CA',
  'Colorado': 'CO',
  'Connecticut': 'CT',
  'Delaware': 'DE',
  'Florida': 'FL',
  'Georgia': 'GA',
  'Hawaii': 'HI',
  'Idaho': 'ID',
  'Illinois': 'IL',
  'Indiana': 'IN',
  'Iowa': 'IA',
  'Kansas': 'KS',
  'Kentucky': 'KY',
  'Louisiana': 'LA',
  'Maine': 'ME',
  'Maryland': 'MD',
  'Massachusetts': 'MA',
  'Michigan': 'MI',
  'Minnesota': 'MN',
  'Mississippi': 'MS',
  'Missouri': 'MO',
  'Montana': 'MT',
  'Nebraska': 'NE',
  'Nevada': 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  'Ohio': 'OH',
  'Oklahoma': 'OK',
  'Oregon': 'OR',
  'Pennsylvania': 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  'Tennessee': 'TN',
  'Texas': 'TX',
  'Utah': 'UT',
  'Vermont': 'VT',
  'Virginia': 'VA',
  'Washington': 'WA',
  'West Virginia': 'WV',
  'Wisconsin': 'WI',
  'Wyoming': 'WY'
};

export const STATE_CODE_TO_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_NAME_TO_CODE).map(([name, code]) => [code, name])
);

/**
 * Convert a state name/abbreviation to its standard 2-character code.
 * Handles: "FL", "fl", "Florida", "florida", "Fl.", "wva", "mich", etc.
 */
export function getStateCode(stateName: string): string {
  if (!stateName) return stateName;

  // Strip periods and extra whitespace
  const cleaned = stateName.replace(/\./g, '').trim();
  if (!cleaned) return stateName;

  // If it's already a valid 2-character code (case-insensitive), normalize to uppercase
  const upper = cleaned.toUpperCase();
  if (cleaned.length === 2 && STATE_CODE_TO_NAME[upper]) {
    return upper;
  }

  // Exact match on full state name
  const exactCode = STATE_NAME_TO_CODE[cleaned];
  if (exactCode) return exactCode;

  // Case-insensitive full name match
  const lower = cleaned.toLowerCase();
  for (const [name, code] of Object.entries(STATE_NAME_TO_CODE)) {
    if (name.toLowerCase() === lower) {
      return code;
    }
  }

  // Common non-standard abbreviations
  const ABBREVIATION_MAP: Record<string, string> = {
    'wva': 'WV', 'w va': 'WV', 'calif': 'CA', 'colo': 'CO',
    'conn': 'CT', 'del': 'DE', 'fla': 'FL', 'ill': 'IL',
    'ind': 'IN', 'kans': 'KS', 'ky': 'KY', 'mich': 'MI',
    'minn': 'MN', 'miss': 'MS', 'mont': 'MT', 'nebr': 'NE',
    'nev': 'NV', 'okla': 'OK', 'oreg': 'OR', 'penn': 'PA',
    'tenn': 'TN', 'tex': 'TX', 'wash': 'WA', 'wis': 'WI',
    'wyo': 'WY', 'mass': 'MA', 'ariz': 'AZ', 'ark': 'AR',
    'ore': 'OR', 'va': 'VA', 'vt': 'VT',
  };
  const abbr = ABBREVIATION_MAP[lower];
  if (abbr) return abbr;

  return stateName; // Return original if not found
}

/**
 * Convert a state code to its full name
 */
export function getStateName(stateCode: string): string {
  const name = STATE_CODE_TO_NAME[stateCode.toUpperCase()];
  return name || stateCode;
}

/**
 * Validate if a string is a valid US state code
 */
export function isValidStateCode(code: string): boolean {
  return code.length === 2 && STATE_CODE_TO_NAME.hasOwnProperty(code.toUpperCase());
}

/**
 * Validate if a string is a valid US state name
 */
export function isValidStateName(name: string): boolean {
  return STATE_NAME_TO_CODE.hasOwnProperty(name);
}